import { Fragment, useMemo } from 'react'
import emojiData from 'emoji-datasource-apple/emoji.json'
import TgEmoji from './TgEmoji'
import MentionChip from './MentionChip'

// Renders a string with native emoji unicode replaced by Apple-style
// sprites (same source the picker uses), so the chat looks identical
// regardless of the underlying OS font (Segoe UI Emoji on Windows vs
// Apple Color Emoji on macOS). Plain-text characters fall through.
//
// OSPROD-49: also auto-linkifies URLs (https://, http://, www.) so they
// render as clickable <a target="_blank"> instead of dead text. Link
// detection runs FIRST — emoji parsing only sees non-URL text — so an
// emoji embedded inside a URL path (rare but valid: paths can contain
// percent-encoded multibyte chars) doesn't fragment the link into
// multiple <a> tags.

interface SpriteMeta {
  unified: string
  sheet_x: number
  sheet_y: number
}

interface TrieNode {
  meta?: SpriteMeta
  children: Map<number, TrieNode>
}

let _trie: TrieNode | null = null

function indexInto(root: TrieNode, unified: string, sheet_x: number, sheet_y: number) {
  const cps = unified
    .split('-')
    .map((p) => parseInt(p, 16))
    .filter((cp) => !Number.isNaN(cp))
  if (cps.length === 0) return
  let node = root
  for (const cp of cps) {
    let next = node.children.get(cp)
    if (!next) {
      next = { children: new Map() }
      node.children.set(cp, next)
    }
    node = next
  }
  node.meta = { unified, sheet_x, sheet_y }
}

function getTrie(): TrieNode {
  if (_trie) return _trie
  const root: TrieNode = { children: new Map() }
  for (const e of emojiData as any[]) {
    if (!e.has_img_apple) continue
    indexInto(root, String(e.unified), e.sheet_x, e.sheet_y)
    if (e.skin_variations) {
      for (const key of Object.keys(e.skin_variations)) {
        const v = e.skin_variations[key]
        if (!v || v.has_img_apple === false) continue
        indexInto(root, String(v.unified), v.sheet_x, v.sheet_y)
      }
    }
  }
  _trie = root
  return root
}

interface Segment {
  type: 'text' | 'emoji' | 'link' | 'mention' | 'mention-everyone'
  value: string
  meta?: SpriteMeta
  /** For link segments: the absolute href to navigate to. May differ
   *  from `value` when the user typed `www.x.com` — display value stays
   *  the bare form, href gets an `https://` prefix. */
  href?: string
  /** For mention segments: the resolved userId (`<@…>` token payload). */
  userId?: string
}

// Mention tokens as written in message content: `<@userId>` for a
// user, literal `@everyone` for broadcast. Authoring is via the
// MentionPicker which inserts the `<@id>` form; the literal `@everyone`
// is typed by the user and matched as-is.
const MENTION_REGEX = /<@([a-zA-Z0-9_-]{1,64})>|@everyone\b/g

// OSPROD-49: detection regex. Matches `https://...`, `http://...` and
// `www.<host>...` runs that don't contain whitespace. Trailing
// punctuation (commonly the sentence-final dot/comma/etc.) is trimmed
// AFTER matching so that "Visit https://example.com." doesn't swallow
// the period into the URL. Anchored to a word boundary on the left so
// patterns like `xhttps://...` or `mailto:foo@https://x.com` (extreme
// edge cases) don't false-match.
const URL_REGEX = /(\bhttps?:\/\/[^\s<>"`]+|\bwww\.[^\s<>"`]+)/gi
// Trailing characters never a meaningful end of a URL. Closing brackets
// are deliberately included only when they're unbalanced inside the
// match — handled in trimUrlTail below, not here.
const URL_TRAILING_PUNCT = /[.,!?;:'"]+$/

function trimUrlTail(raw: string): { url: string; tail: string } {
  let url = raw
  let tail = ''
  // Trim sentence-final punctuation.
  const punctMatch = url.match(URL_TRAILING_PUNCT)
  if (punctMatch) {
    tail = punctMatch[0] + tail
    url = url.slice(0, url.length - punctMatch[0].length)
  }
  // Strip a single unbalanced closing bracket — covers
  // "(see https://x.com/y)" without breaking
  // "https://en.wikipedia.org/wiki/Foo_(bar)".
  for (const [open, close] of [['(', ')'], ['[', ']'], ['{', '}']] as const) {
    if (url.endsWith(close)) {
      const opens = (url.match(new RegExp('\\' + open, 'g')) || []).length
      const closes = (url.match(new RegExp('\\' + close, 'g')) || []).length
      if (closes > opens) {
        tail = close + tail
        url = url.slice(0, -1)
      }
    }
  }
  return { url, tail }
}

function toHref(displayUrl: string): string {
  return /^https?:\/\//i.test(displayUrl) ? displayUrl : `https://${displayUrl}`
}

function toCodepoints(text: string): number[] {
  const out: number[] = []
  for (const ch of text) {
    const cp = ch.codePointAt(0)
    if (cp !== undefined) out.push(cp)
  }
  return out
}

// Internal: emoji-only pass over a substring guaranteed to contain no
// URLs. Same algorithm as before — split out so parseEmoji can compose
// it after URL detection.
function parseEmojiInner(text: string): Segment[] {
  if (!text) return []
  const trie = getTrie()
  const cps = toCodepoints(text)
  const segments: Segment[] = []
  let textBuf = ''
  let i = 0
  while (i < cps.length) {
    let node: TrieNode = trie
    let lastMatch: { meta: SpriteMeta; end: number } | null = null
    let j = i
    while (j < cps.length) {
      const child: TrieNode | undefined = node.children.get(cps[j])
      if (!child) break
      node = child
      j++
      if (node.meta) lastMatch = { meta: node.meta, end: j }
    }
    if (lastMatch) {
      if (textBuf) {
        segments.push({ type: 'text', value: textBuf })
        textBuf = ''
      }
      // Some platforms send a trailing FE0F (variation selector-16)
      // that wasn't part of the canonical sequence. Swallow it so it
      // doesn't appear as a stray box after the sprite.
      let end = lastMatch.end
      if (end < cps.length && cps[end] === 0xfe0f) end++
      const native = cps
        .slice(i, end)
        .map((cp) => String.fromCodePoint(cp))
        .join('')
      segments.push({ type: 'emoji', value: native, meta: lastMatch.meta })
      i = end
    } else {
      textBuf += String.fromCodePoint(cps[i])
      i++
    }
  }
  if (textBuf) segments.push({ type: 'text', value: textBuf })
  return segments
}

// Mid-pass: extract URLs from a chunk that's already been mention-split.
// Same algorithm as before — pulled out so parseEmoji can call it after
// the mention pass.
function parseUrlsAndEmoji(text: string): Segment[] {
  if (!text) return []
  const out: Segment[] = []
  let lastIndex = 0
  URL_REGEX.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = URL_REGEX.exec(text)) !== null) {
    const start = match.index
    const rawUrl = match[0]
    if (start > lastIndex) {
      out.push(...parseEmojiInner(text.slice(lastIndex, start)))
    }
    const { url, tail } = trimUrlTail(rawUrl)
    if (url) {
      out.push({ type: 'link', value: url, href: toHref(url) })
    } else {
      out.push(...parseEmojiInner(rawUrl))
    }
    if (tail) out.push(...parseEmojiInner(tail))
    lastIndex = start + rawUrl.length
  }
  if (lastIndex < text.length) {
    out.push(...parseEmojiInner(text.slice(lastIndex)))
  }
  return out
}

// Public parser. Three-pass: mentions first (so `<@id>` doesn't leak
// into URL/emoji passes), then URLs, then emoji. Preserves order so
// the rendered output matches the source string left-to-right.
export function parseEmoji(text: string): Segment[] {
  if (!text) return []
  const out: Segment[] = []
  let lastIndex = 0
  MENTION_REGEX.lastIndex = 0
  let match: RegExpExecArray | null
  while ((match = MENTION_REGEX.exec(text)) !== null) {
    const start = match.index
    if (start > lastIndex) {
      out.push(...parseUrlsAndEmoji(text.slice(lastIndex, start)))
    }
    if (match[1]) {
      out.push({ type: 'mention', value: match[0], userId: match[1] })
    } else {
      out.push({ type: 'mention-everyone', value: match[0] })
    }
    lastIndex = start + match[0].length
  }
  if (lastIndex < text.length) {
    out.push(...parseUrlsAndEmoji(text.slice(lastIndex)))
  }
  return out
}

interface Props {
  text: string
  /** Sprite display size in CSS pixels. Defaults to 18 (chat bubble line-height). */
  size?: number
  className?: string
}

export default function EmojiText({ text, size = 18, className }: Props) {
  const segments = useMemo(() => parseEmoji(text), [text])
  return (
    <span className={className}>
      {segments.map((s, i) => {
        if (s.type === 'emoji' && s.meta) {
          return (
            <TgEmoji
              key={i}
              unified={s.meta.unified}
              sheetX={s.meta.sheet_x}
              sheetY={s.meta.sheet_y}
              size={size}
            />
          )
        }
        if (s.type === 'mention' && s.userId) {
          return <MentionChip key={i} userId={s.userId} />
        }
        if (s.type === 'mention-everyone') {
          return (
            <span
              key={i}
              className="inline-block px-1.5 rounded bg-discord-blurple/20 text-discord-blurple font-medium"
            >
              @everyone
            </span>
          )
        }
        if (s.type === 'link' && s.href) {
          return (
            // OSPROD-49: open in a new tab, sever the opener so the
            // target page can't navigate this one back. break-all keeps
            // a 200-char URL from blowing out the bubble width;
            // underline + accent colour makes the click target obvious.
            // stopPropagation prevents the click from bubbling into the
            // message-bubble context-menu / reaction handlers.
            <a
              key={i}
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-discord-blurple underline break-all hover:opacity-80"
            >
              {s.value}
            </a>
          )
        }
        return <Fragment key={i}>{s.value}</Fragment>
      })}
    </span>
  )
}
